import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { evaService } from '../../../services/evaService';

const INTENCIONES = [
  { id: 'generar_presupuesto', label: 'Generar Presupuesto', icon: 'FileText', desc: 'Crea un borrador de cotización con partidas' },
  { id: 'agendar_servicio', label: 'Agendar Servicio', icon: 'Calendar', desc: 'Propone horarios y crea cita' },
  { id: 'precio_llanta', label: 'Precio de Llantas', icon: 'Circle', desc: 'Busca llantas por medida en catálogo' },
];

const EvaModal = ({ isOpen, onClose, contexto = 'Dashboard', datosIniciales = {} }) => {
  const [paso, setPaso] = useState('menu'); // menu | form | resultado
  const [intencionSeleccionada, setIntencionSeleccionada] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [copiado, setCopiado] = useState(null);

  // Form fields
  const [medidaLlanta, setMedidaLlanta] = useState('');
  const [marcaLlanta, setMarcaLlanta] = useState('');
  const [motivoCita, setMotivoCita] = useState('');
  const [fechaCita, setFechaCita] = useState('');
  const [hallazgosTexto, setHallazgosTexto] = useState('');

  // Partidas editables del presupuesto
  const [partidas, setPartidas] = useState([]);

  const resetModal = () => {
    setPaso('menu');
    setIntencionSeleccionada(null);
    setResultado(null);
    setError(null);
    setCopiado(null);
    setPartidas([]);
    setMedidaLlanta('');
    setMarcaLlanta('');
    setMotivoCita('');
    setFechaCita('');
    setHallazgosTexto('');
  };

  const handleClose = () => {
    resetModal();
    onClose?.();
  };

  const handleSeleccionarIntencion = (intencion) => {
    setIntencionSeleccionada(intencion);
    if (intencion === 'precio_llanta' || intencion === 'agendar_servicio') {
      setPaso('form');
    } else {
      // generar_presupuesto: ir directo si hay datos, si no mostrar form
      setPaso('form');
    }
  };

  const handleProcesar = async () => {
    setLoading(true);
    setError(null);
    try {
      let datos = { ...datosIniciales };

      if (intencionSeleccionada === 'precio_llanta') {
        if (!medidaLlanta?.trim()) {
          setError('Ingresa la medida de la llanta (ej: 205/55R16)');
          setLoading(false);
          return;
        }
        datos = { medida: medidaLlanta?.trim(), marcaPreferida: marcaLlanta?.trim() || null, vehiculo: datosIniciales?.vehiculo };
      } else if (intencionSeleccionada === 'agendar_servicio') {
        datos = {
          cliente: datosIniciales?.cliente,
          vehiculo: datosIniciales?.vehiculo,
          motivo: motivoCita || 'Servicio general',
          fechaSugerida: fechaCita || null,
        };
      } else if (intencionSeleccionada === 'generar_presupuesto') {
        const hallazgos = hallazgosTexto
          ? hallazgosTexto?.split('\n')?.filter(Boolean)?.map(h => ({ descripcion: h?.trim() }))
          : datosIniciales?.hallazgos || [];
        datos = {
          cliente: datosIniciales?.cliente,
          vehiculo: datosIniciales?.vehiculo,
          hallazgos,
          modulo: contexto,
        };
      }

      const res = await evaService?.procesarIntencion({
        intencion: intencionSeleccionada,
        contexto,
        datos,
      });

      setResultado(res);
      if (res?.partidas_sugeridas?.length > 0) {
        setPartidas(res?.partidas_sugeridas?.map((p, i) => ({ ...p, _id: i })));
      }
      setPaso('resultado');
    } catch (err) {
      setError(err?.message || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleCopiar = async (texto, tipo) => {
    try {
      await navigator.clipboard?.writeText(texto);
      setCopiado(tipo);
      setTimeout(() => setCopiado(null), 2000);
    } catch {
      setCopiado(null);
    }
  };

  const handleGuardarCita = async (slot) => {
    try {
      setLoading(true);
      const fechaHora = `${slot?.fecha}T${slot?.hora_inicio}:00`;
      await evaService?.guardarCita({
        clienteId: datosIniciales?.cliente?.id,
        vehiculoId: datosIniciales?.vehiculo?.id,
        fechaHora,
        motivo: motivoCita || 'Servicio agendado por EVA',
        notas: `Duración estimada: ${slot?.duracion_horas || 2} horas`,
        duracionHoras: slot?.duracion_horas || 2,
      });
      setCopiado('cita_guardada');
      setTimeout(() => setCopiado(null), 3000);
    } catch (err) {
      setError('Error al guardar la cita: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActualizarPartida = (idx, campo, valor) => {
    setPartidas(prev => prev?.map((p, i) => {
      if (i !== idx) return p;
      const updated = { ...p, [campo]: valor };
      if (campo === 'precio_unitario' || campo === 'cantidad') {
        updated.subtotal = Number(updated?.precio_unitario || 0) * Number(updated?.cantidad || 1);
      }
      return updated;
    }));
  };

  const totalPartidas = partidas?.reduce((acc, p) => acc + Number(p?.subtotal || 0), 0);
  const ivaPartidas = totalPartidas * 0.16;
  const totalConIva = totalPartidas + ivaPartidas;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={e => e?.target === e?.currentTarget && handleClose()}>
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Bot" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">EVA — Asistente IA</h2>
              <p className="text-xs text-muted-foreground">Módulo: {contexto}</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="p-5">
          {/* PASO: MENU */}
          {paso === 'menu' && (
            <div>
              <p className="text-sm text-muted-foreground mb-4">¿Qué necesitas? Selecciona una opción:</p>
              <div className="grid grid-cols-1 gap-3">
                {INTENCIONES?.map(intent => (
                  <button
                    key={intent?.id}
                    onClick={() => handleSeleccionarIntencion(intent?.id)}
                    className="flex items-center gap-4 p-4 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all text-left group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon name={intent?.icon} size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm">{intent?.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{intent?.desc}</p>
                    </div>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PASO: FORM */}
          {paso === 'form' && (
            <div>
              <button onClick={() => setPaso('menu')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
                <Icon name="ArrowLeft" size={14} />
                <span>Volver</span>
              </button>

              <h3 className="font-semibold text-foreground mb-4">
                {INTENCIONES?.find(i => i?.id === intencionSeleccionada)?.label}
              </h3>

              {intencionSeleccionada === 'precio_llanta' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Medida de llanta *</label>
                    <input
                      type="text"
                      placeholder="Ej: 205/55R16"
                      value={medidaLlanta}
                      onChange={e => setMedidaLlanta(e?.target?.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Marca preferida (opcional)</label>
                    <input
                      type="text"
                      placeholder="Ej: Michelin, Bridgestone..."
                      value={marcaLlanta}
                      onChange={e => setMarcaLlanta(e?.target?.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              )}

              {intencionSeleccionada === 'agendar_servicio' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Motivo del servicio</label>
                    <input
                      type="text"
                      placeholder="Ej: Cambio de aceite, revisión general..."
                      value={motivoCita}
                      onChange={e => setMotivoCita(e?.target?.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Fecha preferida (opcional)</label>
                    <input
                      type="date"
                      value={fechaCita}
                      onChange={e => setFechaCita(e?.target?.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              )}

              {intencionSeleccionada === 'generar_presupuesto' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Hallazgos / Servicios requeridos</label>
                    <textarea
                      rows={4}
                      placeholder="Escribe los hallazgos o servicios, uno por línea:\nPastillas de freno desgastadas\nDiscos rayados\nCambio de aceite"
                      value={hallazgosTexto}
                      onChange={e => setHallazgosTexto(e?.target?.value)}
                      className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    />
                    {datosIniciales?.hallazgos?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Se usarán los {datosIniciales?.hallazgos?.length} hallazgos del módulo actual si dejas vacío este campo.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-3 mt-5">
                <Button
                  variant="default"
                  onClick={handleProcesar}
                  loading={loading}
                  disabled={loading}
                  iconName="Bot"
                  iconPosition="left"
                  fullWidth
                >
                  {loading ? 'EVA está procesando...' : 'Consultar a EVA'}
                </Button>
              </div>
            </div>
          )}

          {/* PASO: RESULTADO */}
          {paso === 'resultado' && resultado && (
            <div>
              <button onClick={() => setPaso('menu')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors">
                <Icon name="ArrowLeft" size={14} />
                <span>Nueva consulta</span>
              </button>

              {/* Texto resumen */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Icon name="Bot" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground">{resultado?.texto}</p>
                </div>
              </div>

              {resultado?.error && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 mb-4">
                  <p className="text-sm text-warning-foreground">⚠️ {resultado?.texto}</p>
                </div>
              )}

              {/* PRESUPUESTO: tabla editable */}
              {intencionSeleccionada === 'generar_presupuesto' && partidas?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-foreground mb-2">Partidas del Presupuesto (editables)</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-muted">
                          <th className="text-left p-2 border border-border">Descripción</th>
                          <th className="text-center p-2 border border-border w-16">Cant.</th>
                          <th className="text-right p-2 border border-border w-24">P. Unit.</th>
                          <th className="text-right p-2 border border-border w-24">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partidas?.map((p, i) => (
                          <tr key={i} className="hover:bg-muted/50">
                            <td className="p-1 border border-border">
                              <input
                                type="text"
                                value={p?.descripcion || ''}
                                onChange={e => handleActualizarPartida(i, 'descripcion', e?.target?.value)}
                                className="w-full bg-transparent text-xs text-foreground focus:outline-none focus:bg-background px-1 py-0.5 rounded"
                              />
                            </td>
                            <td className="p-1 border border-border">
                              <input
                                type="number"
                                min="1"
                                value={p?.cantidad || 1}
                                onChange={e => handleActualizarPartida(i, 'cantidad', e?.target?.value)}
                                className="w-full bg-transparent text-xs text-foreground text-center focus:outline-none focus:bg-background px-1 py-0.5 rounded"
                              />
                            </td>
                            <td className="p-1 border border-border">
                              <input
                                type="number"
                                min="0"
                                value={p?.precio_unitario || 0}
                                onChange={e => handleActualizarPartida(i, 'precio_unitario', e?.target?.value)}
                                className="w-full bg-transparent text-xs text-foreground text-right focus:outline-none focus:bg-background px-1 py-0.5 rounded"
                              />
                            </td>
                            <td className="p-1 border border-border text-right text-muted-foreground">
                              ${Number(p?.subtotal || 0)?.toLocaleString('es-MX')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-muted/50">
                          <td colSpan={3} className="p-2 border border-border text-right text-xs font-medium">Subtotal:</td>
                          <td className="p-2 border border-border text-right text-xs">${totalPartidas?.toLocaleString('es-MX')}</td>
                        </tr>
                        <tr className="bg-muted/50">
                          <td colSpan={3} className="p-2 border border-border text-right text-xs">IVA (16%):</td>
                          <td className="p-2 border border-border text-right text-xs">${ivaPartidas?.toLocaleString('es-MX')}</td>
                        </tr>
                        <tr className="bg-primary/10">
                          <td colSpan={3} className="p-2 border border-border text-right text-sm font-bold">Total:</td>
                          <td className="p-2 border border-border text-right text-sm font-bold">${totalConIva?.toLocaleString('es-MX')}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Acciones del presupuesto */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      onClick={() => handleCopiar(
                        partidas?.map(p => `${p?.descripcion} x${p?.cantidad} = $${p?.subtotal}`)?.join('\n') + `\nTotal: $${totalConIva?.toLocaleString('es-MX')}`,
                        'whatsapp'
                      )}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-success/10 text-success border border-success/20 rounded-lg hover:bg-success/20 transition-colors"
                    >
                      <Icon name={copiado === 'whatsapp' ? 'Check' : 'MessageCircle'} size={13} />
                      {copiado === 'whatsapp' ? '¡Copiado!' : 'Copiar para WhatsApp'}
                    </button>
                    <button
                      onClick={() => handleCopiar(
                        partidas?.map(p => `${p?.descripcion}\t${p?.cantidad}\t$${p?.precio_unitario}\t$${p?.subtotal}`)?.join('\n') + `\nTotal: $${totalConIva?.toLocaleString('es-MX')}`,
                        'email'
                      )}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary/10 text-primary border border-primary/20 rounded-lg hover:bg-primary/20 transition-colors"
                    >
                      <Icon name={copiado === 'email' ? 'Check' : 'Mail'} size={13} />
                      {copiado === 'email' ? '¡Copiado!' : 'Copiar para Email'}
                    </button>
                  </div>
                </div>
              )}

              {/* LLANTAS: opciones */}
              {intencionSeleccionada === 'precio_llanta' && resultado?.opciones?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-foreground mb-2">Opciones disponibles</h4>
                  <div className="space-y-2">
                    {resultado?.opciones?.map((op, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">{op?.marca} {op?.modelo}</p>
                          <p className="text-xs text-muted-foreground">{op?.medida} · {op?.proveedor} · Exist: {op?.existencia} pzas</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-foreground">${Number(op?.precio_venta)?.toLocaleString('es-MX')}</p>
                          <p className="text-xs text-muted-foreground">Costo: ${Number(op?.costo)?.toLocaleString('es-MX')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {resultado?.texto_whatsapp && (
                    <button
                      onClick={() => handleCopiar(resultado?.texto_whatsapp, 'wa_llantas')}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-success/10 text-success border border-success/20 rounded-lg hover:bg-success/20 transition-colors mt-3"
                    >
                      <Icon name={copiado === 'wa_llantas' ? 'Check' : 'MessageCircle'} size={13} />
                      {copiado === 'wa_llantas' ? '¡Copiado!' : 'Copiar texto para WhatsApp'}
                    </button>
                  )}
                </div>
              )}

              {/* AGENDA: slots */}
              {intencionSeleccionada === 'agendar_servicio' && resultado?.slots_sugeridos?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-sm text-foreground mb-2">Horarios sugeridos</h4>
                  <div className="space-y-2">
                    {resultado?.slots_sugeridos?.map((slot, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-muted/50">
                        <div>
                          <p className="text-sm font-medium text-foreground">{slot?.fecha} · {slot?.hora_inicio} – {slot?.hora_fin}</p>
                          <p className="text-xs text-muted-foreground">{slot?.duracion_horas}h estimadas · {slot?.nota}</p>
                        </div>
                        <button
                          onClick={() => handleGuardarCita(slot)}
                          disabled={loading}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                          <Icon name={copiado === 'cita_guardada' ? 'Check' : 'CalendarPlus'} size={13} />
                          {copiado === 'cita_guardada' ? '¡Guardada!' : 'Confirmar'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Acciones recomendadas */}
              {resultado?.acciones_recomendadas?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Acciones recomendadas:</p>
                  <ul className="space-y-1">
                    {resultado?.acciones_recomendadas?.map((a, i) => (
                      <li key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Icon name="ChevronRight" size={12} className="text-primary" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvaModal;
